import { memo, type ReactNode, useMemo } from 'react';

import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';

import { SearchCommandBoardList } from '~/components/SearchCommand/SearchCommandBoardList';
import { SearchCommandListFrame } from '~/components/SearchCommand/SearchCommandListFrame';
import { SearchCommandSavedFilterList } from '~/components/SearchCommand/SearchCommandSavedFilterList';
import { SearchCommandSuggestionList } from '~/components/SearchCommand/SearchCommandSuggestionList';

export type SearchCommandListProps = {
  hideSuggestions: boolean;
  isOpen: boolean;
  listStart: ReactNode | undefined;
  onBoardClick: (board: SearchBoardsResponse) => void;
  onSavedFilterSelect: (savedFilter: SearchSavedFiltersResponse) => void;
  onSuggestionClick: (suggestion: string) => void;
  renderCustomContent?: () => ReactNode;
  searchValue?: string;
  suggestions?: SuggestionsResponse;
};

export const SearchCommandList = memo(
  ({
    hideSuggestions,
    isOpen,
    listStart,
    onBoardClick,
    onSavedFilterSelect,
    onSuggestionClick,
    renderCustomContent,
    searchValue,
    suggestions,
  }: SearchCommandListProps) => {
    const showSearchCommandList = useMemo(() => {
      const hasSearchTerm = searchValue && searchValue.trim().length > 0;
      const hasSuggestions = !!suggestions?.suggestions?.length;
      const hasSavedFilters = !!suggestions?.savedFilters?.length;
      const hasBoards = !!suggestions?.boards?.length;
      const hasSearchResults = hasSuggestions || hasSavedFilters || hasBoards;
      const hasCustomContent = !!renderCustomContent;

      const shouldShowForSearch = isOpen && !hideSuggestions && (hasSearchTerm || hasSearchResults);

      const shouldShowForCustomContent = isOpen && hasCustomContent;

      return shouldShowForSearch || shouldShowForCustomContent;
    }, [isOpen, hideSuggestions, searchValue, suggestions, renderCustomContent]);

    if (!showSearchCommandList) {
      return null;
    }

    return (
      <SearchCommandListFrame>
        {renderCustomContent ? (
          renderCustomContent()
        ) : (
          <>
            <SearchCommandSuggestionList onSelect={onSuggestionClick} suggestions={suggestions?.suggestions} />
            {listStart}

            {!!suggestions?.savedFilters.length && !!onSavedFilterSelect && (
              <SearchCommandSavedFilterList onSelect={onSavedFilterSelect} savedFilters={suggestions.savedFilters} />
            )}

            {!!suggestions?.boards.length && (
              <SearchCommandBoardList boards={suggestions.boards} onBoardClick={onBoardClick} />
            )}
          </>
        )}
      </SearchCommandListFrame>
    );
  },
);

SearchCommandList.displayName = 'SearchCommandList';
